import React, { useContext, useEffect, useState } from "react";

import { DraggableData, DraggableEvent } from "react-draggable";
import { ElementState, Preview } from "@creatomate/preview";

import styled from "styled-components";
import { useTimelineScale } from "components/stories/video-studio/contexts/TimelineScaleContext";
import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import { useActiveElementContext } from "../../context/ActiveElementContext";
import { DragContext } from "../../context/DragContext";
import Draggable from "../../Draggable";
import { validateTrimMagnet } from "../utils";
import { TrimContext } from "../../context/TrimContext";

const Main = styled.div<{ width: number }>`
  position: absolute;
  height: 100%;
  width: ${props => `${props.width}px`};
  cursor: ew-resize;
  z-index: 30;
`;

interface ResizeHandleProps {
  element: ElementState;
  side: "start" | "end";
  time: number;
  onChange: (time: number, duration: number) => void;
  onNotChange: (time: number, duration: number) => void;
  onComplete: () => void;
  preview: Preview;
  onValidate: (time: number, duration: number, side: "start" | "end") => boolean;
  isVideo?: boolean;
  smallElement: boolean;
}

const ResizeHandle: React.FC<ResizeHandleProps> = ({
  element, side, time, onChange, onComplete, onNotChange, preview, isVideo, smallElement, onValidate
}) => {
  const { data: { activeElementsId }, handleUpdateData } = useContext(CreatomateContext);
  const { timelineScale } = useTimelineScale();
  const { setTrimmingElement } = useContext(TrimContext);
  const { setIsDragging } = useContext(DragContext);

  const [width, setWidth] = useState(20);
  const [gridX, setGridX] = useState(1);
  const [_snapTime, setSnapTime] = useState(-1);
  const [magnetThreshold, setMagnetThreshold] = useState(0.25);
  const TIMELINE_SCALE_INITIAL_VALUE = 65;
  const MAGNET_INITIAL_THRESHOLD = 0.25;
  const activeElementContext = useActiveElementContext();

  const active = activeElementsId.includes(element.source.id);

  useEffect(
    () => {
      if (smallElement) {
        setWidth(5);
      } else {
        setWidth(isVideo ? 30 : 20);
      }
    }, [isVideo, smallElement]
  );

  const dragStart = (timeOffset: number) => {
    let duration = Math.max(element.duration - timeOffset, 0.5);
    const endTime = element.localTime + element.duration;
    let time = duration === 0.5 ? endTime - duration : element.localTime + timeOffset;
    const isValidTrim = validateTrimMagnet(
      activeElementsId,
      element,
      duration,
      preview.state?.elements || [],
      magnetThreshold,
      setSnapTime,
      side,
      time
    );
    if (isValidTrim) {
      setGridX(15);
      setSnapTime(currentSnapTime => {
        if (currentSnapTime >= 0) {
          const endTime = element.localTime + element.duration;
          time = currentSnapTime;
          duration = endTime - time;
        }
        return currentSnapTime;
      });
    } else {
      setGridX(1);
    }
    if (time < 0) {
      duration += time;
      time = 0;
    }
    if (onValidate(time, duration, side)) {
      onChange(time, duration);
    } else {
      onNotChange(time, duration);
    }
  };

  const dragEnd = (timeOffset: number) => {
    const duration = Math.max(element.duration + timeOffset, 0.5);
    if (onValidate(element.localTime, duration, side)) {
      onChange(element.localTime, duration);
    } else {
      onNotChange(time, duration);
    }
  };

  const selectActiveElement = () => {
    if (!activeElementsId.includes(element.source.id)) {
      preview.setActiveElements([element.source.id]);
    }
  };

  const handleDrag = (_event: DraggableEvent, data: DraggableData, context: any) => {
    const timeOffset = (data.x - context.startX) / timelineScale;
    if (side === "start") {
      dragStart(timeOffset);
    } else {
      dragEnd(timeOffset);
    }
  };

  return (
    <Draggable
      grid={[gridX, 25]}
      onStart={(_, data) => {
        if (!active) handleUpdateData({ activeElementsId: [element.source.id] });
        let promise = Promise.resolve();
        if (!active) promise = preview.setActiveElements([element.source.id]);
        promise.then(() => setTrimmingElement(element)).catch(_error => {});
        activeElementContext.setTrim(true);
        setMagnetThreshold(
          (TIMELINE_SCALE_INITIAL_VALUE * MAGNET_INITIAL_THRESHOLD) / timelineScale
        );
        return { startX: data.x };
      }}
      onDrag={handleDrag}
      onStop={() => {
        onComplete();
        selectActiveElement();
        setIsDragging(false);
        activeElementContext.setTrim(false);
      }}
    >
      {ref => (
        <Main
          ref={ref}
          style={{ left: time * timelineScale - (side === "end" ? width : 0) }}
          width={width}
        />
      )}
    </Draggable>
  );
};

export default ResizeHandle;
